<script setup lang="ts">
import { onBeforeMount, ref, watchEffect } from 'vue';
import FlowNavigationButton from '../ui/FlowNavigationButton.vue';
import RegionDropdown from '../ui/RegionDropdown.vue';
import { storeToRefs } from 'pinia';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { Regions, type SupportedRegion } from '@/models/Regions';
import Loading from '@/components/ui/Loading.vue';
import Mixpanel from '@/integrations/Mixpanel';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';

const emptyRegion = { code: '', title: '' };

const isLoading = ref(true);

const { kycSession } = storeToRefs(useKycSessionStore());

if (!kycSession.value) {
  throw new Error('Kyc session not found');
}

const disabled = ref<boolean>(false);
const dropdownOptions = ref<SupportedRegion[]>([]);
const dropdownValue = ref<SupportedRegion>(emptyRegion);

async function loadRegions() {
  if (!kycSession.value) {
    throw new Error('Kyc session not found');
  }
  const { partnerAccountId, region } = kycSession.value;
  const regions = Regions.forAccount(partnerAccountId);
  await regions.load();

  dropdownOptions.value = regions.availableRegions;
  const selectedRegion =
    regions.availableRegions.find((r) => r.code === region?.toUpperCase()) || emptyRegion;
  dropdownValue.value = selectedRegion;
}

onBeforeMount(async () => {
  await loadRegions();
  isLoading.value = false;
});

watchEffect(() => {
  disabled.value =
    isLoading.value || dropdownOptions.value.length === 0 || dropdownValue.value.code === '';
});

async function updateKycSessionRegion() {
  isLoading.value = true;

  if (kycSession.value?.region?.toUpperCase() !== dropdownValue.value.code) {
    await kycSession.value?.updateRegion(dropdownValue.value.code);
  }

  isLoading.value = false;

  return true;
}

function trackRegionSelection(region: SupportedRegion) {
  Mixpanel.instance.trackCountrySelection(region.code);
}
</script>

<template>
  <KycSessionLayout>
    <div class="flex flex-col" data-test="screen-kyc-region">
      <h1 class="mb-4 text-3xl">Where do you live?</h1>
      <p class="text-light-secondary dark:text-dark-secondary mb-6">
        Select the country where you permanently reside. You'll need to provide a valid ID for the
        selected country.
      </p>
      <div class="my-6">
        <Loading class="mx-auto mt-10 h-10 w-10" v-if="isLoading" />
        <RegionDropdown
          v-else
          :dropdown-options="dropdownOptions"
          :disabled="disabled"
          v-model:dropdown-value="dropdownValue"
          @update:dropdown-value="trackRegionSelection"
        />
      </div>
    </div>
    <template #button-bar>
      <FlowNavigationButton
        :preNavigationAction="updateKycSessionRegion"
        text="Next"
        direction="forward"
        :disabled="disabled"
      />
    </template>
  </KycSessionLayout>
</template>
